import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WifiOff, X } from 'lucide-react';
import { useCanAccess } from '@/hooks/useCanAccess';
import { useWhatsAppAutomationRisk } from '@/hooks/useWhatsApp';

/**
 * RC-209 — com o WhatsApp da clínica desconectado, pedido de confirmação e lembretes não saem,
 * e ninguém via isso na tela. O aviso nasce do estado (instância desconectada + agenda nos
 * próximos 2 dias), só para quem pode reconectar. Recolher vale até o dia seguinte: enquanto
 * continuar desconectado, a clínica reencontra o aviso amanhã.
 */

export const WHATSAPP_DISCONNECTED_NOTICE_KEY = 'valiosas.whatsappDisconnectedNotice.dismissedOn';

const todayKey = () => new Date().toISOString().slice(0, 10);

function readDismissed(): boolean {
  try {
    return localStorage.getItem(WHATSAPP_DISCONNECTED_NOTICE_KEY) === todayKey();
  } catch {
    return false;
  }
}

export function WhatsAppDisconnectedNoticeBanner() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const canManage = useCanAccess('whatsapp:manage');
  const { data: risk } = useWhatsAppAutomationRisk(canManage);
  const [dismissed, setDismissed] = useState(readDismissed);

  if (!canManage || dismissed || !risk) return null;
  if (pathname.replace(/\/+$/, '').toLowerCase() === '/config/whatsapp') return null;
  // Mora no SHELL: payload fora do formato vira "nada a avisar", nunca tela branca.
  const upcoming = Number(risk.upcoming) || 0;
  if (risk.disconnected !== true || upcoming <= 0) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(WHATSAPP_DISCONNECTED_NOTICE_KEY, todayKey());
    } catch {
      // Sem armazenamento, o aviso só some até recarregar.
    }
    setDismissed(true);
  };

  const impact =
    upcoming === 1
      ? '1 mensagem automática dos próximos 2 dias não vai sair.'
      : `${upcoming} mensagens automáticas dos próximos 2 dias não vão sair.`;

  return (
    <div role="status" className="flex flex-wrap items-start gap-3 border-b border-sky-200 bg-sky-50 px-4 py-3 text-so-14 text-sky-950 lg:px-8">
      <WifiOff className="mt-0.5 size-4 shrink-0 text-sky-700" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="font-bold">O WhatsApp da clínica está desconectado.</div>
        <p className="mt-1 text-sky-900">{impact}</p>
        <div className="mt-2">
          <button
            type="button"
            onClick={() => navigate('/config/whatsapp')}
            // min-h-11 = 44px: no celular este é um alvo de dedo, não de mouse.
            className="min-h-11 rounded-so-sm bg-sky-900 px-3 py-1.5 text-so-12 font-bold text-white hover:bg-sky-800 sm:min-h-0"
          >
            Reconectar WhatsApp
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Recolher aviso"
        className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-so-sm px-2 text-so-12 font-bold text-sky-700 hover:bg-sky-100"
      >
        <X className="size-4" />
        Recolher
      </button>
    </div>
  );
}
